import { zodResolver } from '@hookform/resolvers/zod'
import {
  type V2CreateAdminRepresentativeInput,
  V2CreateAdminRepresentativeSchema
} from '@idc-forms/schemas/api-v2/admin-representatives'
import { Controller, useForm } from 'react-hook-form'
import { useSocietiesV2LookupQuery } from '@/features/societies/hooks/use-societies-v2-lookup-query'
import { CATALOG_CIVIL_STATUS } from '@/shared/catalogs/civil-status'
import countries from '@/shared/catalogs/countries.json'
import departments from '@/shared/catalogs/departments-gt.json'
import { CATALOG_GENDER } from '@/shared/catalogs/gender'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { COUNTRY_LOCAL } from '@/shared/constants/config'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

type CountryOption = { isoCode: string; name: { es: string } }
type DepartmentOption = { code: number; name: string }

const COUNTRY_OPTIONS = countries as CountryOption[]
const DEPARTMENT_OPTIONS = departments as DepartmentOption[]

type RepresentativeV2FormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<V2CreateAdminRepresentativeInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: V2CreateAdminRepresentativeInput) => void
  onCancel: () => void
}

/**
 * Formulario de representante legal (crear/editar) contra la API v2, para el side
 * panel. Espeja el form v1 —mismos campos, mismas secciones— y sólo cambia el
 * contrato: la sociedad viaja por `soc_` (del lookup v2), no por ObjectId. RHF +
 * zodResolver con el schema v2 (`@idc-forms/schemas/api-v2/admin-representatives`),
 * el MISMO que valida el backend; los textos legales se normalizan a MAYÚSCULAS en
 * el schema.
 */
export function RepresentativeV2Form({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: RepresentativeV2FormProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<V2CreateAdminRepresentativeInput>({
    resolver: zodResolver(V2CreateAdminRepresentativeSchema),
    defaultValues: {
      active: true,
      address: { country: COUNTRY_LOCAL.isoCode },
      ...defaultValues
    }
  })

  const { data: societies = [] } = useSocietiesV2LookupQuery()
  const isCountryLocal = watch('address.country') === COUNTRY_LOCAL.isoCode

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="society"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.society)}>
                <FieldLabel htmlFor="representative-society">Sociedad</FieldLabel>
                <NativeSelect
                  id="representative-society"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona una sociedad
                  </NativeSelectOption>
                  {societies.map((society) => (
                    <NativeSelectOption key={society.id} value={society.id}>
                      {society.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldDescription>
                  Cada sociedad tiene un único representante legal.
                </FieldDescription>
                <FieldError errors={[errors.society]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="representative-name">Nombre completo</FieldLabel>
                <Input
                  id="representative-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="identification"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.identification)}>
                <FieldLabel htmlFor="representative-identification">
                  Documento Personal de Identificación
                </FieldLabel>
                <Input
                  id="representative-identification"
                  inputMode="numeric"
                  maxLength={13}
                  placeholder="0000 00000 0000"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value.replace(/\D/g, ''))
                  }}
                  autoComplete="off"
                />
                <FieldError errors={[errors.identification]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="position"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.position)}>
                <FieldLabel htmlFor="representative-position">Puesto</FieldLabel>
                <Input
                  id="representative-position"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.position]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="gender"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.gender)}>
                <FieldLabel htmlFor="representative-gender">Género</FieldLabel>
                <NativeSelect
                  id="representative-gender"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona un género
                  </NativeSelectOption>
                  {CATALOG_GENDER.map((option) => (
                    <NativeSelectOption key={option.value} value={option.value}>
                      {option.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.gender]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="civilStatus"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.civilStatus)}>
                <FieldLabel htmlFor="representative-civil-status">Estado civil</FieldLabel>
                <NativeSelect
                  id="representative-civil-status"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona un estado civil
                  </NativeSelectOption>
                  {CATALOG_CIVIL_STATUS.map((option) => (
                    <NativeSelectOption key={option.value} value={option.value}>
                      {option.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.civilStatus]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="nationality"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.nationality)}>
                <FieldLabel htmlFor="representative-nationality">Nacionalidad</FieldLabel>
                <NativeSelect
                  id="representative-nationality"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona un país
                  </NativeSelectOption>
                  {COUNTRY_OPTIONS.map((country) => (
                    <NativeSelectOption key={country.isoCode} value={country.isoCode}>
                      {country.name.es}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.nationality]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="profession"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.profession)}>
                <FieldLabel htmlFor="representative-profession">Profesión u oficio</FieldLabel>
                <Input
                  id="representative-profession"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.profession]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="birthdate"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.birthdate)}>
                <FieldLabel htmlFor="representative-birthdate">Fecha de nacimiento</FieldLabel>
                <Input
                  id="representative-birthdate"
                  type="date"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
                <FieldError errors={[errors.birthdate]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Dirección</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="address.address"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.address?.address)}>
                <FieldLabel htmlFor="representative-address">Dirección</FieldLabel>
                <Input
                  id="representative-address"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.address?.address]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="address.country"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.address?.country)}>
                <FieldLabel htmlFor="representative-country">País</FieldLabel>
                <NativeSelect
                  id="representative-country"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona un país
                  </NativeSelectOption>
                  {COUNTRY_OPTIONS.map((country) => (
                    <NativeSelectOption key={country.isoCode} value={country.isoCode}>
                      {country.name.es}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.address?.country]} />
              </Field>
            )}
          />

          {isCountryLocal && (
            <Controller
              control={control}
              name="address.department"
              render={({ field }) => (
                <Field data-invalid={Boolean(errors.address?.department)}>
                  <FieldLabel htmlFor="representative-department">Departamento</FieldLabel>
                  <NativeSelect
                    id="representative-department"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      field.onChange(e.target.value)
                    }}
                  >
                    <NativeSelectOption value="" disabled>
                      Selecciona un departamento
                    </NativeSelectOption>
                    {DEPARTMENT_OPTIONS.map((department) => (
                      <NativeSelectOption key={department.code} value={String(department.code)}>
                        {department.name}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                  <FieldError errors={[errors.address?.department]} />
                </Field>
              )}
            />
          )}
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Notario</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="notary.name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.notary?.name)}>
                <FieldLabel htmlFor="representative-notary-name">Nombre del notario</FieldLabel>
                <Input
                  id="representative-notary-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.notary?.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="notary.gender"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.notary?.gender)}>
                <FieldLabel htmlFor="representative-notary-gender">Género del notario</FieldLabel>
                <NativeSelect
                  id="representative-notary-gender"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona un género
                  </NativeSelectOption>
                  {CATALOG_GENDER.map((option) => (
                    <NativeSelectOption key={option.value} value={option.value}>
                      {option.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.notary?.gender]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Nombramiento en registro mercantil</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="appointmentMercantileRegister.date"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.appointmentMercantileRegister?.date)}>
                <FieldLabel htmlFor="representative-appointment-date">
                  Fecha de nombramiento
                </FieldLabel>
                <Input
                  id="representative-appointment-date"
                  type="date"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
                <FieldError errors={[errors.appointmentMercantileRegister?.date]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="appointmentMercantileRegister.number"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.appointmentMercantileRegister?.number)}>
                <FieldLabel htmlFor="representative-appointment-number">Número</FieldLabel>
                <Input
                  id="representative-appointment-number"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.appointmentMercantileRegister?.number]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="appointmentMercantileRegister.sheet"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.appointmentMercantileRegister?.sheet)}>
                <FieldLabel htmlFor="representative-appointment-sheet">Folio</FieldLabel>
                <Input
                  id="representative-appointment-sheet"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.appointmentMercantileRegister?.sheet]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="appointmentMercantileRegister.book"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.appointmentMercantileRegister?.book)}>
                <FieldLabel htmlFor="representative-appointment-book">Libro</FieldLabel>
                <Input
                  id="representative-appointment-book"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.appointmentMercantileRegister?.book]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Estado</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="representative-active">Activo</FieldLabel>
                <Switch
                  id="representative-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
